import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableContainer from '@mui/material/TableContainer'
import { DocListItem } from 'entities/Documents'
import { useSearchParams } from 'react-router-dom'

import TableHead from './TableHead'
import TableRow from './TableRow'

function parseSort(paramOrder: string): { orderBy: string; order: 'asc' | 'desc' } {
  const [orderBy, order] = paramOrder.split(':')
  return { orderBy, order: order === 'desc' ? 'desc' : 'asc' }
}

function composeSort({ orderBy, order }) {
  return `${orderBy}:${order}`
}

type Props = {
  rows: DocListItem[]
  selectedIds: string[]
  setSelectedIds: (ids: string[]) => void
  openRenameDialog?: (oID: string, name: string) => void
}

function DocumentsTable(props: Props) {
  const [searchParams, setSearchParams] = useSearchParams()
  const { rows, selectedIds, setSelectedIds } = props

  const { order, orderBy } = parseSort(searchParams.get('sort') || '')

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc'
    const nextOrder = isAsc ? 'desc' : 'asc'
    const sort = composeSort({
      order: nextOrder,
      orderBy: property,
    })
    searchParams.set('sort', sort)
    setSearchParams(searchParams)
  }

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelectedIds = rows.map((n) => n.objectID)
      setSelectedIds(newSelectedIds)
      return
    }
    setSelectedIds([])
  }

  const handleCheckChange = (objectID, checked) => {
    if (checked) {
      setSelectedIds([...selectedIds, objectID])
    } else {
      setSelectedIds(selectedIds.filter((id) => id !== objectID))
    }
  }

  const isSelected = (name) => selectedIds.indexOf(name) !== -1

  const tableRows = rows.map((row) => {
    const { objectID } = row
    const isItemSelected = isSelected(objectID)

    return (
      <TableRow
        key={objectID}
        row={row}
        selected={isItemSelected}
        onCheckChange={handleCheckChange}
        openRenameDialog={props.openRenameDialog}
      />
    )
  })

  return (
    <TableContainer>
      <Table
        stickyHeader
        sx={{ whiteSpace: 'nowrap' }}
        aria-labelledby="tableTitle"
        size={'medium'}
        aria-label="enhanced table"
      >
        <TableHead
          numSelected={selectedIds.length}
          order={order}
          orderBy={orderBy}
          onSelectAllClick={handleSelectAllClick}
          onRequestSort={handleRequestSort}
          rowCount={rows.length}
        />
        <TableBody data-testid="table-body-documents-list">{tableRows}</TableBody>
      </Table>
    </TableContainer>
  )
}

export default DocumentsTable
